import Vue from "vue";
import Router from "vue-router";
import store from "@/store";
import { updateGAPagePath } from "@/mixins/eventTracking";

import NotFound from "@/views/NotFound.vue";
import GetQuote from "@/views/GetQuote.vue";
import Referral from "@/views/Referral.vue";
import ShortSummary from "@/views/ShortSummary.vue";
import PersonalDetails from "@/views/PersonalDetails.vue";
import AddressPicker from "@/views/AddressPicker";
import TenancyLength from "@/views/TenancyLength";
import BroadBandDate from "@/views/BroadBandDate";
import FullSummary from "@/views/FullSummary";
import BankDetails from "@/views/BankDetails.vue";
import Congratulations from "@/views/Congratulations.vue";
import JoiningSummary from "@/views/joining-tenant/JoiningSummary";
import AccountAlreadyComplete from "@/views/joining-tenant/AccountAlreadyComplete";
import JoiningPersonalDetails from "@/views/joining-tenant/JoiningPersonalDetails";
import HouseComplete from "@/views/joining-tenant/HouseComplete";
import AccountAlreadyExists from "@/views/AccountAlreadyExists";
import PreSignup from "@/views/PreSignup";
import BroadbandUpgradePicker from "@/views/BroadbandUpgradePicker";

Vue.use(Router);

const router = new Router({
  mode: "history",
  base: process.env.BASE_URL,
  routes: [
    {
      path: "/",
      name: "presignup",
      component: PreSignup,
      beforeEnter({ query }, from, next) {
        if (query.ref) {
          next({
            path: "/referral",
            query: { ref: query.ref },
          });
        } else {
          next();
        }
      },
    },
    {
      path: "/household",
      name: "quote",
      component: GetQuote,
    },
    {
      path: "/referral",
      name: "referral",
      component: Referral,
      beforeEnter({ query }, from, next) {
        if (query.ref) {
          next();
        } else {
          next("/");
        }
      },
    },
    {
      path: "/broadband",
      name: "BroadbandUpgradePicker",
      component: BroadbandUpgradePicker,
      meta: {
        hasGreyBG: true,
      },
      beforeEnter(to, from, next) {
        if (store.getters.canSkipBroadbandSelectionPage) {
          next("/short-summary");
        } else if (store.state.products.length) {
          next();
        } else {
          next("/");
        }
      },
    },
    {
      path: "/short-summary",
      name: "short-summary",
      component: ShortSummary,
      meta: {
        hasGreyBG: true,
      },
      beforeEnter({ query }, from, next) {
        const hasAddressOrPostcode = query.address || query.postcode;
        if (store.state.products.length) {
          next();
        } else if (hasAddressOrPostcode && query.housemates) {
          next();
        } else {
          next("/");
        }
      },
    },
    {
      path: "/your-address",
      name: "address",
      component: AddressPicker,
      beforeEnter(to, from, next) {
        if (store.state.products.length) {
          next();
        } else {
          next("/");
        }
      },
    },
    {
      path: "/your-details",
      name: "personal-details",
      component: PersonalDetails,
      beforeEnter(to, from, next) {
        if (
          store.state.houseInformation.address.hasOwnProperty("guid") ||
          store.state.houseInformation.address.hasOwnProperty("fullAddress")
        ) {
          next();
        } else {
          next("/");
        }
      },
    },
    {
      path: "/tenancy",
      name: "tenancy-dates",
      component: TenancyLength,
      beforeEnter(to, from, next) {
        if (store.state.personalDetails.firstname) {
          next();
        } else {
          next("/");
        }
      },
    },
    {
      path: "/broadband-date",
      name: "broadbandDate",
      component: BroadBandDate,
      beforeEnter(to, from, next) {
        if (store.state.houseInformation.startDate) {
          next();
        } else {
          next("/");
        }
      },
    },
    {
      path: "/full-summary",
      name: "fullSummary",
      component: FullSummary,
      meta: {
        hasGreyBG: true,
      },
      beforeEnter(to, from, next) {
        if (store.state.houseInformation.startDate) {
          next();
        } else {
          next("/");
        }
      },
    },
    {
      path: "/direct-debit",
      name: "bank-details",
      props: true,
      component: BankDetails,
      beforeEnter(to, from, next) {
        if (store.state.houseInformation.startDate) {
          next();
        } else {
          next("/");
        }
      },
    },
    {
      path: "/congratulations",
      name: "congratulations",
      component: Congratulations,
      meta: {
        hasGreyBG: true,
      },
      beforeEnter(to, from, next) {
        if (store.state.bankDetails.accountNumber) {
          next();
        } else {
          next("/");
        }
      },
    },
    {
      path: "/account-already-exists",
      name: "account-already-exists",
      props: true,
      component: AccountAlreadyExists,
    },
    {
      path: "/joining-tenant",
      name: "joining-tenant",
      component: JoiningSummary,
      meta: {
        hasGreyBG: true,
      },
      beforeEnter({ query }, from, next) {
        if (query.guid) {
          next();
        } else {
          next("/");
        }
      },
    },
    {
      path: "/joining-tenant/house-already-complete",
      name: "house-already-complete",
      props: true,
      component: AccountAlreadyComplete,
    },
    {
      path: "/joining-tenant/personal-details",
      name: "joining-personal-details",
      component: JoiningPersonalDetails,
      beforeEnter(to, from, next) {
        if (store.state.accountInformation.accountCreator) {
          next();
        } else {
          next("/");
        }
      },
    },
    {
      path: "/joining-tenant/direct-debit",
      name: "joining-bank-details",
      props: true,
      component: BankDetails,
      beforeEnter(to, from, next) {
        if (store.state.personalDetails.firstname) {
          next();
        } else {
          next("/");
        }
      },
    },
    {
      path: "/joining-tenant/congratulations",
      name: "joining-congratulations",
      component: Congratulations,
      meta: {
        hasGreyBG: true,
      },
      beforeEnter(to, from, next) {
        if (store.state.bankDetails.accountNumber) {
          next();
        } else {
          next("/");
        }
      },
    },
    {
      path: "/joining-tenant/house-complete",
      name: "house-complete",
      component: HouseComplete,
      meta: {
        hasGreyBG: true,
      },
      beforeEnter(to, from, next) {
        if (store.state.bankDetails.accountNumber) {
          next();
        } else {
          next("/");
        }
      },
    },
    {
      path: "*",
      name: "404",
      component: NotFound,
    },
    // {
    //   path: "/lazy-load-example",
    //   name: "lazy-load-example",
    //   // route level code-splitting
    //   // this generates a separate chunk (lazy-load-example.[hash].js) for this route
    //   // which is lazy-loaded when the route is visited.
    //   component: () => {
    //     import(/* webpackChunkName: "package" */ "./views/lazy-load-example.vue");
    //   }
    // },
  ],
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition && to.path !== "/") {
      return savedPosition;
    } else {
      return { x: 0, y: 0 };
    }
  },
});

// Add a class name to body tag when in products route
router.beforeEach((to, from, next) => {
  const bodyEl = document.querySelector("body");
  const hadGreyBG = from.meta.hasGreyBG || false;
  const hasGreyBG = to.meta.hasGreyBG || false;

  if (!hadGreyBG && hasGreyBG) {
    bodyEl.classList.add("bg-grey");
  } else if (hadGreyBG && !hasGreyBG) {
    bodyEl.classList.remove("bg-grey");
  }

  next();
});

// Analytics page view update
router.afterEach((to) => {
  updateGAPagePath(to.path);
});

export default router;
